import type { ReactNode } from "react"

import { clsx } from "clsx"

import type { SolarComputeHostingPlan } from "~/api/dto/solar-proposal-compute.dto"
import type { SolarComputeBillShareDto } from "~/api/dto/solar-proposal-savings.dto"
import {
  SOLAR_AI_COMPUTE_FAQ,
  SOLAR_AI_COMPUTE_HOW_IT_WORKS,
} from "~/constants/solar-ai-compute-copy.constants"

import { formatComputeSpecs } from "./solar-ai-compute-copy"
import {
  ComputeNodeStory,
  NetworkBonusPanel,
  type ComputePlanFigures,
} from "./solar-compute-node-story"
import type { SolarPresentationAiCompute } from "./solar-presentation-data"
import { formatNumber } from "./solar-proposal-design"
import {
  toShownComputePayout,
  type ShownComputePayout,
} from "./solar-proposal-savings"
import {
  Eyebrow,
  GradText,
  Panel,
  Section,
  SectionHeading,
} from "../BatteryOnlyProposal/primitives"
import { formatUsd, formatUsdExact } from "../BatteryOnlyProposal/view-helpers"
import {
  AiComputeFaq,
  HowItWorksPanel,
  LedgerRow,
  NodeHeroPanel,
  ShareSplit,
  Stat,
  VendorAppCard,
} from "../ProposalPresentation/ai-compute/AiComputeParts"

const PLAN_NAMES: Record<SolarComputeHostingPlan, string> = {
  core: `Core`,
  plus: `Plus`,
  max: `Max`,
}

/** The bonus's swatch: striped, because it may be $0. */
const BONUS_SWATCH = `bg-[image:repeating-linear-gradient(135deg,var(--bp-success)_0_3px,transparent_3px_6px)] ring-1 ring-inset ring-bp-success/40`

type Payout = ShownComputePayout

function LedgerTag({
  tone,
  children,
}: {
  tone: `muted` | `success`
  children: ReactNode
}): JSX.Element {
  return (
    <span
      className={clsx(
        `ml-1.5 inline-block rounded-full px-2 py-0.5 align-middle text-[10.5px] font-semibold tracking-[0.06em] uppercase`,
        tone === `success`
          ? `bg-bp-success-soft text-bp-success`
          : `bg-bp-elevated text-bp-text-muted ring-1 ring-bp-border`,
      )}
    >
      {children}
    </span>
  )
}

/** Option B: the guaranteed base, the bonus at its cap, and the "up to" total. */
function PaymentLedger({
  payout,
  nodeLoadKwhPerYear,
}: {
  payout: Payout
  nodeLoadKwhPerYear: number | null
}): JSX.Element {
  return (
    <div data-testid="solar-ai-compute-ledger">
      <Eyebrow className="mb-4">Your monthly hosting payment</Eyebrow>
      <LedgerRow
        label="Hosting plan"
        note="set in your hosting schedule"
        value={PLAN_NAMES[payout.plan]}
      />
      <div data-testid="solar-ai-compute-base">
        <LedgerRow
          label="Fixed monthly base"
          tag={<LedgerTag tone="success">Guaranteed</LedgerTag>}
          note={`from activation, every month of the ${payout.termYears}-year term`}
          value={formatUsdExact(payout.monthlyBase)}
          tone="success"
          rule
        />
      </div>
      <div data-testid="solar-ai-compute-bonus-cap">
        <LedgerRow
          label="+ Network performance bonus"
          tag={<LedgerTag tone="muted">Varies</LedgerTag>}
          note="the most it can be · may be $0"
          value={formatUsdExact(payout.monthlyBonusCap)}
        />
      </div>
      {nodeLoadKwhPerYear != null ? (
        <LedgerRow
          label="Node electricity"
          note="savings modeled at 90% utilization"
          value={`${formatNumber(nodeLoadKwhPerYear, 0)} kWh/yr`}
        />
      ) : null}
      <div className="flex items-baseline justify-between gap-4 border-t-2 border-bp-text-soft pt-4">
        <div>
          <div className="text-[15px] font-semibold text-bp-text">
            Your monthly payment
          </div>
          <div className="mt-0.5 text-[12px] text-bp-text-dim">
            base + bonus, before taxes
          </div>
        </div>
        <div
          data-testid="solar-ai-compute-monthly"
          className="font-display text-[length:var(--step-4)] leading-none font-semibold tracking-[-0.02em] tabular-nums"
        >
          <span className="mr-2 align-middle text-[14px] font-medium tracking-normal text-bp-text-muted">
            up to
          </span>
          <GradText>{formatUsdExact(payout.monthlyMax)}</GradText>
        </div>
      </div>
      {/* The legend beneath carries the split for assistive tech. */}
      <div
        aria-hidden="true"
        className="mt-4 flex h-3 gap-0.5 overflow-hidden rounded-full bg-bp-elevated"
      >
        <div
          className="basis-0 bg-bp-success"
          style={{ flexGrow: payout.monthlyBase }}
        />
        <div
          className={clsx(`basis-0`, BONUS_SWATCH)}
          style={{ flexGrow: payout.monthlyBonusCap }}
        />
      </div>
      <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1 text-[12.5px] text-bp-text-muted">
        <span className="inline-flex items-center gap-2">
          <span
            aria-hidden="true"
            className="size-2.5 rounded-sm bg-bp-success"
          />
          {formatUsd(payout.monthlyBase)} guaranteed base
        </span>
        <span className="inline-flex items-center gap-2">
          <span
            aria-hidden="true"
            className={clsx(`size-2.5 rounded-sm`, BONUS_SWATCH)}
          />
          up to {formatUsd(payout.monthlyBonusCap)} bonus
        </span>
      </div>
    </div>
  )
}

/**
 * The payment beside year one's monthly cost: the bill after solar plus the
 * solar payment. The gateway splits only the base; the bonus sits beside it
 * at its cap and is never added into what's covered or left over. Without a
 * payment (cash, or a deferred first year) the cost reads as the bill alone.
 */
function PaymentBesideCost({
  billAfterSolarMonthly,
  solarPaymentMonthly,
  monthlyCost,
  share,
  payout,
}: {
  billAfterSolarMonthly: number
  solarPaymentMonthly: number
  monthlyCost: number
  share: NonNullable<SolarComputeBillShareDto[`base`]>
  payout: Payout
}): JSX.Element {
  const hasPayment = solarPaymentMonthly > 0
  const parts = [
    {
      label: hasPayment ? `Toward your bill + payment` : `Toward your est. bill`,
      value: share.coveredCost,
      swatch: `bg-bp-gold`,
    },
    {
      label: `Base left over`,
      value: share.leftOver,
      swatch: `bg-bp-success`,
    },
    {
      label: `Bonus, up to`,
      value: payout.monthlyBonusCap,
      swatch: BONUS_SWATCH,
    },
  ] as const

  return (
    <div data-testid="solar-ai-compute-share" className="flex flex-col gap-4">
      <Eyebrow>Where your payment can go · illustrative</Eyebrow>
      <p className="font-display text-[22px] leading-snug font-medium tracking-[-0.01em] text-bp-text-soft">
        Up to{` `}
        <span className="font-semibold text-bp-success">
          {formatUsd(payout.monthlyMax)}/mo
        </span>
        {` `}— a {formatUsd(share.monthlyBase)} base plus bonus — beside your
        estimated{` `}
        <span className="font-semibold text-bp-text">
          {formatUsd(monthlyCost)}/mo
        </span>
        {` `}
        {hasPayment
          ? `utility bill and solar payment.`
          : `utility bill after solar.`}
      </p>
      {hasPayment ? (
        <p className="-mt-2 text-[13px] text-bp-text-muted">
          {formatUsd(billAfterSolarMonthly)} utility bill after solar +{` `}
          {formatUsd(solarPaymentMonthly)} solar payment
        </p>
      ) : null}
      <ShareSplit parts={parts} />
      <p className="rounded-xl border border-bp-border bg-bp-elevated px-4 py-3.5 text-[13px] leading-relaxed text-bp-text-muted">
        An illustration. You can authorize Vendor to pay part or all of your
        payment toward your electricity, internet, solar, financing or battery
        bills; what it pays to a bill comes out of the same payment, not on top
        of it. Only the base is guaranteed; the bonus may be $0.
      </p>
    </div>
  )
}

/**
 * The compute node section (Hosting Agreement Rev 2.10, Option B · B3): the
 * selected Vendor node, what a compute node is, how the network bonus works,
 * and its monthly hosting payment, read-only. The node is chosen in the
 * Compute setup step; an editor's page passes `action` to link there. Every
 * figure is the gateway's payout, formatted only; the only bonus figure is its
 * cap. Without a payout (money withheld, an older gateway, or no package
 * data) the product and the explanations still show.
 */
export function SolarAiComputeSection({
  aiCompute,
  billShare = null,
  savingsComputeYears = null,
  action = null,
}: {
  aiCompute: SolarPresentationAiCompute
  /** The savings read's base split at the bill after solar plus the payment; null shows no comparison. */
  billShare?: SolarComputeBillShareDto | null
  /** The savings years whose figures count the base; null when the page's savings count none. */
  savingsComputeYears?: number | null
  action?: ReactNode
}): JSX.Element {
  const { name, computerData, income } = aiCompute
  const payout = toShownComputePayout(aiCompute.payout)
  const planName = payout ? PLAN_NAMES[payout.plan] : null
  const figures: ComputePlanFigures | null =
    payout && planName
      ? {
          planName,
          base: formatUsd(payout.monthlyBase),
          bonusCap: formatUsd(payout.monthlyBonusCap),
          termYears: payout.termYears,
        }
      : null
  // The savings read's split, shown only when its base is the ledger's: two
  // reads that disagree show no comparison rather than mismatched figures.
  const besideBill =
    payout && billShare?.base?.monthlyBase === payout.monthlyBase
      ? {
          billAfterSolarMonthly: billShare.billAfterSolarMonthly,
          solarPaymentMonthly: billShare.solarPaymentMonthly,
          monthlyCost: billShare.monthlyCost,
          share: billShare.base,
          payout,
        }
      : null
  const specs = formatComputeSpecs(computerData)

  return (
    <Section id="ai-compute">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <SectionHeading
          eyebrow="Optional add-on · Compute node"
          sub={
            figures && payout
              ? `Host a Vendor compute node at home. Vendor pays you a fixed ${figures.base} every month for ${payout.termYears} years, plus a network bonus of up to ${figures.bonusCap} when the network earns it.`
              : `Host a Vendor compute node at home. Vendor pays you a fixed monthly base for 30 years, plus a network bonus when the network earns it.`
          }
        >
          A fixed monthly payment that can{` `}
          <GradText>help pay your bills</GradText>.
        </SectionHeading>
        {action ? <div className="mb-9">{action}</div> : null}
      </div>

      <div className="mb-3.5 grid grid-cols-1 gap-3.5 lg:grid-cols-[1.45fr_1fr]">
        <NodeHeroPanel
          name={name}
          specs={specs}
          testId="solar-ai-compute-name"
          badge={planName ? `${planName} Hosting Plan` : null}
        />
        <HowItWorksPanel
          steps={Object.values(SOLAR_AI_COMPUTE_HOW_IT_WORKS)}
          footer={`${payout?.termYears ?? 30}-year term from activation · Vendor maintains and upgrades the node at no cost`}
        />
      </div>

      <ComputeNodeStory figures={figures} />
      <NetworkBonusPanel figures={figures} />

      {payout ? (
        <>
          <Panel
            className={clsx(
              `mb-3.5 grid grid-cols-1 gap-10 rounded-[20px] p-6 lg:gap-12 lg:p-8`,
              // One column, the ledger capped, when there is no bill to set it beside.
              besideBill && `lg:grid-cols-2`,
            )}
          >
            <div className={besideBill ? undefined : `max-w-[640px]`}>
              <PaymentLedger
                payout={payout}
                nodeLoadKwhPerYear={income?.nodeLoadKwhPerYear ?? null}
              />
            </div>
            {besideBill ? <PaymentBesideCost {...besideBill} /> : null}
          </Panel>

          <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-[1.25fr_1fr]">
            <Panel className="grid grid-cols-1 content-center gap-6 rounded-[20px] p-6 sm:grid-cols-3 lg:p-7">
              <Stat
                label="Monthly payment"
                value={`up to ${formatUsd(payout.monthlyMax)}`}
                note={`${formatUsd(payout.monthlyBase)} guaranteed + up to ${formatUsd(payout.monthlyBonusCap)} bonus`}
              />
              <Stat
                label={`${payout.termYears}-year total`}
                value={`up to ${formatUsd(payout.maxTermTotal)}`}
                note={`${formatUsd(payout.baseTermTotal)} of it guaranteed`}
              />
              <Stat
                label="Term"
                value={`${payout.termYears} yr`}
                note="from activation · no-cost upgrades"
              />
            </Panel>
            <VendorAppCard title="Track your base, bonus and payouts in the Vendor app." />
          </div>
        </>
      ) : (
        <>
          <div data-testid="solar-ai-compute-unavailable" className="mb-3.5">
            <Panel className="rounded-[20px] p-6 text-[length:var(--step-0)] text-bp-text-muted lg:p-8">
              {aiCompute.payout
                ? `Payment figures unavailable`
                : `Payment figures are being updated`}
            </Panel>
          </div>
          <VendorAppCard title="Track your base, bonus and payouts in the Vendor app." />
        </>
      )}

      <AiComputeFaq
        entries={Object.values(SOLAR_AI_COMPUTE_FAQ)}
        idPrefix="solar-ai-compute"
      />

      <p
        data-testid="solar-ai-compute-disclosure"
        className="mt-4 max-w-[1100px] text-[11.5px] leading-relaxed text-bp-text-muted"
      >
        Illustrative figures based on the Vendor Hosting Agreement, Revision
        2.10.
        {figures && payout
          ? ` The ${figures.planName} Hosting Plan pays a fixed ${figures.base} monthly base from activation for the ${payout.termYears}-year service term; the variable network performance bonus is capped at ${figures.bonusCap} a month, is calculated from network-wide collected receipts under Section 2.2, and may be $0. Totals that include the bonus show its cap, not an estimate.`
          : ` The fixed monthly base is paid from activation for the 30-year service term; the variable network performance bonus is capped by Hosting Plan, is calculated from network-wide collected receipts under Section 2.2, and may be $0.`}
        {savingsComputeYears
          ? ` Your savings estimate counts only the base, in each of your ${savingsComputeYears} savings years.`
          : null}
        {` `}
        Node electricity is modeled at 90% utilization and included in your
        savings estimate. Payments may be taxable; consult a tax advisor.
      </p>
    </Section>
  )
}
